import { useState, useEffect, useCallback } from 'react';

/**
 * useReCaptchaV3
 *
 * Loads Google reCAPTCHA v3 script asynchronously and provides a function to execute actions.
 * - SSR safe
 * - Exposes error and ready state
 * - Type safe
 *
 * @param siteKey - Your reCAPTCHA v3 site key
 * @returns { executeReCaptcha, isReady, error }
 */
type UseReCaptchaV3Result = {
	executeReCaptcha: (action: string) => Promise<string | null>;
	isReady: boolean;
	error: string | null;
};

const useReCaptchaV3 = (siteKey: string): UseReCaptchaV3Result => {
	const [isReCaptchaReady, setIsReCaptchaReady] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		// SSR safety
		if (typeof window === 'undefined') return;

		if ((window as any).grecaptcha) {
			setIsReCaptchaReady(true);
		} else {
			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
			script.async = true;
			script.onerror = () => setError('Failed to load reCAPTCHA script');
			document.head.appendChild(script);
			script.onload = () => setIsReCaptchaReady(true);
		}

		return () => {
			// Optionally remove script if needed
			// window.onRecaptchaLoad = null;
		};
	}, [siteKey]);

	const executeReCaptcha = useCallback(
		async (action: string) => {
			if (typeof window === 'undefined') return null;
			if (isReCaptchaReady && (window as any).grecaptcha) {
				try {
					return await (window as any).grecaptcha.execute(siteKey, { action });
				} catch (e) {
					setError('reCAPTCHA execution failed');
					return null;
				}
			}
			return null;
		},
		[isReCaptchaReady, siteKey],
	);

	return { executeReCaptcha, isReady: isReCaptchaReady, error };
};

export default useReCaptchaV3;
